import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewChild
} from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import Hls from 'hls.js';
import { isBeta } from '../../shared/services';
import { ControllerService } from '../../shared/services/controller.service';
import { BunnyMapService } from '../../shared/services/bunny-map.service';
import { StoreHelper } from '../../shared/helpers/store.helper';
import { ReferencesService } from '../../references.service';

@Component({
  selector: 'app-bunny-video',
  templateUrl: './bunny-video.component.html',
  styleUrls: ['./bunny-video.component.scss'],
  standalone: false
})
export class BunnyVideoComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild('nextButton', { static: false }) nextButton!: ElementRef;
  @ViewChild('videoEl', { static: true }) videoElRef!: ElementRef<HTMLVideoElement>;

  private get video(): HTMLVideoElement {
    return this.videoElRef.nativeElement;
  }

  showReferencesFlag: boolean = false;
  current: number = 0;
  duration: number = 0;
  progressValue: number = 0;

  header: string = '';
  description: string = '';

  underPlay: boolean = false;
  OnCaption: boolean = false;
  textToComplete: string = 'Next';
  showNext: boolean = false;

  isReplay: boolean = false;
  skipToEnd: boolean = false;
  vimeoId: string = '';
  directBunnyId: string = '';

  firstLoad: boolean = false;
  started: boolean = false;
  mediaFinished: boolean = false;

  lastUpdated: number = 0;
  updateInterval: number = 1000;
  lastEvent: number = 0;

  activeNext: boolean = false;
  isBeta: boolean = isBeta();
  posterUrl: string = '';

  private hls: Hls | null = null;
  private qpListener: any = null;

  constructor(
    private route: ActivatedRoute,
    public controller: ControllerService,
    private bunnyMapService: BunnyMapService,
    private references: ReferencesService
  ) {}

  ngOnDestroy(): void {
    this.qpListener?.unsubscribe();
    this.hls?.destroy();
    document.querySelectorAll<HTMLElement>('.main-link').forEach(el => el.style.display = '');
    this.controller.toggleLoading(false);
  }

  ngAfterViewInit(): void {
    this.focusNext();
  }

  focusNext() {
    if (this.showNext && this.activeNext) {
      window.setTimeout(() => this.nextButton?.nativeElement.focus(), 0);
    }
  }

  ngOnInit() {
    this.skipToEnd = StoreHelper.getObject('fastForward');

    this.qpListener = this.route.queryParams.subscribe((queryParams: Params) => {
      const videoComponent = this.controller.getSectionComponents()[0];

      if (videoComponent) {
        this.vimeoId = videoComponent.properties.vimeoId;
        this.directBunnyId = videoComponent.properties.bunnyId || '';
        this.header = videoComponent.elements.find((it: any) => it.type === 'header').value;
        this.description = videoComponent.elements.find((it: any) => it.type === 'p').value;
        this.isReplay = videoComponent.properties.isReplay === 'true' || this.controller.getPreviewing();
        this.showReferencesFlag = !!this.controller.getReferencePageForVideoId(this.vimeoId);
        this.showNext = this.isReplay && !this.controller.isLibrary();
      } else {
        alert('Error loading section, no video component specified.');
      }

      this.activeNext = this.isReplay;

      if (this.isReplay) {
        window.setTimeout(() => this.focusNext(), 250);
      }

      this.controller.toggleLoading(false);
      this.loadVideo(true);
      this.videobgEnlarge();

      document.getElementById('video-with-controls')?.addEventListener('fullscreenchange', () => {
        if (document.fullscreenElement === null) {
          this.underPlay = false;
          this.video.pause();
        }
      });
    });
  }

  private loadVideo(firstLoad = false) {
    this.firstLoad = firstLoad;
    this.lastEvent = new Date().getTime();
    this.controller.toggleLoading(true);

    const bunnyId = this.directBunnyId || this.bunnyMapService.getBunnyId(this.vimeoId);
    const src = this.bunnyMapService.getPlaylistUrl(bunnyId);
    this.posterUrl = this.bunnyMapService.getThumbnailUrl(bunnyId);

    this.hls?.destroy();

    if (Hls.isSupported()) {
      this.hls = new Hls();
      this.hls.loadSource(src);
      this.hls.attachMedia(this.video);
    } else {
      this.video.src = src;
    }

    const captionTrack = document.createElement('track');
    captionTrack.kind = 'captions';
    captionTrack.label = 'English';
    captionTrack.srclang = 'GB';
    captionTrack.src = this.bunnyMapService.getCaptionsUrl(bunnyId);
    captionTrack.addEventListener('error', () => {
      captionTrack.src = this.bunnyMapService.getCaptionsUrl(bunnyId, 'EN');
    }, { once: true });
    this.video.appendChild(captionTrack);

    this.video.volume = 1;

    this.video.addEventListener('timeupdate', () => {
      this.controller.toggleLoading(false);
      const current = this.video.currentTime;
      const duration = this.video.duration;
      const percent = duration ? current / duration : 0;

      if (this.lastEvent && this.lastEvent < new Date().getTime() - 5000) {
        this.lastEvent = new Date().getTime();
        this.fadeOutControls();
      }

      if (!this.lastUpdated || this.lastUpdated + this.updateInterval < Date.now()) {
        this.lastUpdated = Date.now();
        this.onMediaTimeUpdate({ seconds: current, percent, duration });
      }
    });

    this.video.addEventListener('loadedmetadata', () => {
      this.controller.toggleLoading(false);
      this.onMediaTimeUpdate({ seconds: 0, percent: 0, duration: this.video.duration });
    });

    this.video.addEventListener('ended', () => {
      this.current = this.duration;
      this.onMediaEnd();
    });
  }

  onMediaTimeUpdate(data: { seconds: number; percent: number; duration: number }) {
    if (data.percent >= 0) {
      this.controller.toggleLoading(false);
    }
    this.mediaFinished = false;
    this.current = data.seconds;
    this.duration = data.duration;
    this.progressValue = data.percent * 100;
  }

  onClickProgress(e: MouseEvent) {
    const target = e.target as HTMLElement;
    const x = e.pageX - target.offsetLeft;
    const clickedValue = x / target.clientWidth;
    if (clickedValue < 1) {
      const newTime = Math.floor(clickedValue * this.duration);
      this.video.currentTime = newTime;
      this.onMediaTimeUpdate({ seconds: newTime, duration: this.duration, percent: newTime / this.duration });
    }
  }

  @HostListener('document:mousemove')
  onMouseMove() {
    if (!this.mediaFinished) {
      this.lastEvent = new Date().getTime();
      this.fadeInControls();
    }
  }

  @HostListener('document:click')
  onMouseClick() {
    if (!this.mediaFinished) {
      this.lastEvent = new Date().getTime();
      this.fadeInControls();
    }
  }

  onMediaEnd() {
    this.mediaFinished = true;
    document.querySelectorAll<HTMLElement>('.main-link').forEach(el => el.style.display = '');
    this.underPlay = false;
    this.showNext = true;
    this.activeNext = true;
    document.exitFullscreen().catch(() => {});

    if (!this.isReplay) {
      this.controller.next(false, false, true);
    } else {
      this.focusNext();
    }
  }

  async onPlay() {
    this.started = true;
    document.querySelector('.play-next-wrapper')?.classList.add('hide');
    this.underPlay = true;
    try {
      await this.video.play();
      this.videobgEnlarge();
      setTimeout(() => this.videobgEnlarge(), 10);
      document.querySelectorAll<HTMLElement>('.main-link').forEach(el => el.style.display = 'none');
      document.getElementById('video-with-controls')?.requestFullscreen();
      if (this.skipToEnd) {
        this.video.currentTime = this.duration - 2;
      }
    } catch {
      this.onPause();
    }
  }

  onPause() {
    this.underPlay = false;
    this.video.pause();
    document.querySelectorAll<HTMLElement>('.main-link').forEach(el => el.style.display = '');
    document.exitFullscreen().catch(() => {});
  }

  onBackward() {
    if (this.underPlay && this.current - 5 > 0) {
      this.current -= 5;
      this.video.currentTime = this.current;
    }
  }

  onForward() {
    if (this.underPlay && (this.current + 5) < this.duration) {
      this.current += 5;
      this.video.currentTime = this.current;
    }
  }

  OnSubtitles() {
    const tracks = this.video.textTracks;
    for (let i = 0; i < tracks.length; i++) {
      const track = tracks[i];
      if (track.kind === 'subtitles' || track.kind === 'captions') {
        track.mode = 'showing';
        break;
      }
    }
    this.OnCaption = true;
  }

  OffSubtitles() {
    const tracks = this.video.textTracks;
    for (let i = 0; i < tracks.length; i++) {
      tracks[i].mode = 'disabled';
    }
    this.OnCaption = false;
  }

  onNext() {
    this.controller.next();
  }

  @HostListener('window:resize')
  onResize() {
    setTimeout(() => this.videobgEnlarge(), 10);
  }

  videobgEnlarge() {
    const videoBgAspect = document.querySelector<HTMLElement>('.fullScreen .videobg-aspect');
    const videoBgWidth = document.querySelector<HTMLElement>('.fullScreen .videobg-width');
    const controlContainer = document.querySelector<HTMLElement>('.fullScreen .videobg-width .controls-container');
    if (!videoBgAspect || !videoBgWidth || !controlContainer) return;

    const videoAspect = videoBgAspect.offsetHeight / videoBgAspect.offsetWidth;
    const windowAspect = window.innerHeight / window.innerWidth;
    const calculated = 100 * (videoAspect - (windowAspect - 0.06));
    controlContainer.style.bottom = Math.max(calculated, 10) + '%';

    if (windowAspect > videoAspect && !this.underPlay) {
      const calculationWith = (windowAspect / videoAspect) * 100;
      videoBgWidth.style.width = calculationWith + '%';
      if (calculationWith > 100) {
        controlContainer.style.width = '100%';
        videoBgWidth.style.width = '100%';
      }
    } else {
      if (this.underPlay) {
        videoBgWidth.style.width = '100%';
      }
    }
  }

  fadeOutControls() {
    const el = document.querySelector<HTMLElement>('.controls-container');
    if (el && el.style.opacity !== '0') {
      el.style.opacity = '0';
    }
  }

  fadeInControls() {
    const el = document.querySelector<HTMLElement>('.controls-container');
    if (el && el.style.opacity !== '1') {
      el.style.opacity = '1';
    }
  }

  checkControls(event: KeyboardEvent) {
    if (event.key === 'Tab') {
      this.fadeInControls();
    }
  }

  showReferences() {
    this.references.showReferences(this.vimeoId);
  }
}
